import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { applyTextSize, getTextSize, type TextSize } from '../../platform/textSize';
import { getLang, setLang, t, type Lang } from '../../platform/lang';

/**
 * Who to call.
 *
 * Deliberately static and part of the app shell, so it works with no signal —
 * which is exactly when a driver needs it. The order on screen IS the escalation
 * order: a driver skim-reading at a loading dock should reach the right person
 * without deciding anything.
 *
 * Numbers live here rather than in the database on purpose: this list must
 * survive an offline start with an empty cache, and it changes about once a year.
 */

interface Contact {
  name: string;
  /**
   * A few words, not a sentence. A driver scans this; they do not read it.
   *
   * Bilingual, because this is the screen a stuck driver is sent to and
   * "which of these three do I call about my pay" is the entire job of it.
   */
  role: { en: string; es: string };
  numbers: string[];
}

export const CONTACTS: Contact[] = [
  { name: 'Mauricio Castillo',
    role: { en: 'App problems · Backups', es: 'Problemas con la app · Reemplazos' },
    numbers: ['(321) 240-9103', '(917) 745-3208'] },
  { name: 'Camila Pizarro',
    role: { en: 'Manager · Pay, hours, time off',
            es: 'Gerente · Pago, horas, días libres' },
    numbers: ['(510) 909-6279'] },
  { name: 'John Whetstone',
    role: { en: 'Owner · If no answer above',
            es: 'Dueño · Si no contestan arriba' },
    numbers: ['(510) 909-5996'] },
];

/** tel:/sms: need bare digits; US numbers get the country code. */
export function dial(display: string): string {
  const digits = display.replace(/\D/g, '');
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
}

export function HelpScreen() {
  const navigate = useNavigate();

  return (
    <main className="screen">
      <button className="btn link" onClick={() => navigate('/route')}>&larr; Route</button>
      <h1>{t('needHelp')}</h1>
      <p className="hint">{t('helpTapToCall')}</p>

      {/*
        * The numbers come first, and they are the whole point of this screen.
        * Somebody opening it is almost always trying to reach the office, often
        * standing at a door with a problem. Everything else waits.
        */}
      {CONTACTS.map((c) => (
        <section key={c.name} className="help-block">
          <h2>{c.name}</h2>
          <p className="help-role">{c.role[getLang()]}</p>
          <div className="help-numbers">
            {c.numbers.map((n) => (
              <div className="row" key={n}>
                {/* The number is the label. "Call" added width, not meaning. */}
                <a className="btn primary" href={`tel:${dial(n)}`}
                   aria-label={`Call ${c.name} on ${n}`}>{n}</a>
                <a className="btn secondary" href={`sms:${dial(n)}`}
                   aria-label={`Text ${c.name} on ${n}`}>{t('textThem')}</a>
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="help-block">
        <h2>{t('covTitle')}</h2>
        <Link className="btn secondary" to="/coverage">{t('covOpen')}</Link>
      </section>

      <section className="help-block">
        <h2>{t('guideOpen')}</h2>
        <Link className="btn secondary" to="/guide">{t('guideOpenBtn')}</Link>
        <Link className="btn secondary" to="/check">{getLang() === 'es' ? 'Autoprueba de la app' : 'App self-test'}</Link>
      </section>

      {/* Set once, then never again — so they sit at the bottom. */}
      <LanguagePicker />
      <TextSizePicker />
    </main>
  );
}

/**
 * Bigger text, chosen here.
 *
 * On the Help screen deliberately: a driver who cannot read the screen is
 * already looking for help, and this is the first place they are told to go.
 * Burying it in a settings menu would mean the people who need it most never
 * find it.
 *
 * The buttons show their own effect — each is rendered at the size it sets — so
 * it can be used without reading anything.
 */
function TextSizePicker() {
  const [size, setSize] = useState<TextSize>(getTextSize);

  const pick = (s: TextSize) => { setSize(s); applyTextSize(s); };

  const OPTIONS: Array<{ id: TextSize; label: string; px: string }> = [
    { id: 'normal', label: t('sizeNormal'),  px: '1rem' },
    { id: 'large',  label: t('sizeLarge'),   px: '1.15rem' },
    { id: 'xlarge', label: t('sizeBiggest'), px: '1.32rem' },
  ];

  return (
    <section className="help-block">
      <h2>{t('textSizeLabel')}</h2>
      <p className="hint">{t('textSizeHint')}</p>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {OPTIONS.map((o) => (
          <button
            key={o.id}
            className={size === o.id ? 'btn primary' : 'btn secondary'}
            style={{ fontSize: o.px, flex: '1 1 30%' }}
            aria-pressed={size === o.id}
            onClick={() => pick(o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="muted small">{t('textSizeKeeps')}</p>
    </section>
  );
}

/**
 * Language, changeable here as well as in the first-run briefing.
 *
 * A driver who tapped through the briefing in the wrong language has no other
 * way back to it — the briefing is shown once. Help is where they are told to
 * go when something is wrong, so the switch lives here too.
 *
 * Only the security briefing and the face scan screens are translated so far,
 * which the note says plainly rather than letting someone switch to Spanish and
 * wonder why most of the app did not change.
 */
function LanguagePicker() {
  const [lang, setLangState] = useState<Lang>(getLang);
  const pick = (l: Lang) => { setLangState(l); setLang(l); };

  return (
    <section className="help-block">
      <h2>{t('langLabel', undefined, lang)}</h2>
      <p className="hint">{t('langHint', undefined, lang)}</p>
      <div className="row" style={{ gap: 8 }}>
        <button className={lang === 'en' ? 'btn primary' : 'btn secondary'}
                style={{ flex: 1 }} aria-pressed={lang === 'en'}
                onClick={() => pick('en')}>English</button>
        <button className={lang === 'es' ? 'btn primary' : 'btn secondary'}
                style={{ flex: 1 }} aria-pressed={lang === 'es'}
                onClick={() => pick('es')}>Español</button>
      </div>
    </section>
  );
}
